import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from './utils'

const VARIANT_COUNT_BG: Record<string, string> = {
  gray: 'bg-gradient-to-br from-gray-500 to-gray-700',
  yellow: 'bg-gradient-to-br from-yellow-500 to-amber-600',
  green: 'bg-gradient-to-br from-green-500 to-green-700',
  orange: 'bg-gradient-to-br from-orange-500 to-orange-700',
  blue: 'bg-gradient-to-br from-blue-500 to-blue-700',
  red: 'bg-gradient-to-br from-red-500 to-red-700',
  purple: 'bg-gradient-to-br from-purple-500 to-purple-700',
}

/** Darker gradient + border when selected (e.g. filter chips). Keys match gradient `variant`s. */
const VARIANT_PRESSED: Record<string, string> = {
  gray: 'from-gray-400/40 to-gray-600/28 border-gray-600/60 text-gray-800',
  yellow:
    'from-yellow-400/45 to-amber-600/28 border-amber-600/60 text-amber-800',
  green: 'from-green-400/45 to-green-700/28 border-green-700/60 text-green-800',
  orange:
    'from-orange-400/45 to-orange-600/28 border-orange-600/60 text-orange-800',
  blue: 'from-blue-400/45 to-blue-600/28 border-blue-600/60 text-blue-800',
  red: 'from-red-400/45 to-red-600/28 border-red-600/60 text-red-800',
}

const badgeVariants = cva(
  'w-fit inline-flex items-center rounded-full border transition-colors',
  {
    variants: {
      size: {
        sm: 'px-1.5 py-0.5 text-[10px]',
        default: 'px-2 py-1 text-xs',
        md: 'px-2.5 py-1.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
      clickable: {
        true: 'cursor-pointer hover:opacity-80 active:opacity-70',
        false: '',
      },
      variant: {
        default: 'bg-primary/10 text-primary border-primary/20',
        primary: 'bg-primary text-primary-foreground',
        secondary: 'button-secondary',
        light: 'bg-gray-100/50 border-white/30',
        destructive: 'bg-red-600 text-white border-red-700 shadow-new',
        outline: 'border-border text-foreground bg-transparent',
        glass: 'glass-no-shadow border border-gray-200',
        glassBig: 'bg-white/80 p-2 text-base font-light',
        ghost: 'border-none text-foreground bg-transparent px-1',
        orange:
          'bg-gradient-to-br from-orange-300/20 to-orange-500/12 text-orange-600 border-orange-500/40',
        blue: 'bg-gradient-to-br from-blue-300/20 to-blue-500/12 text-blue-600 border-blue-500/40',
        green:
          'bg-gradient-to-br from-green-300/20 to-green-500/12 text-green-600 border-green-500/40',
        gray: 'bg-gradient-to-br from-gray-300/20 to-gray-500/12 text-gray-600 border-gray-500/40',
        red: 'bg-gradient-to-br from-red-300/20 to-red-500/12 text-red-600 border-red-500/40',
        yellow:
          'bg-gradient-to-br from-yellow-300/20 to-yellow-500/12 text-yellow-600 border-yellow-500/40',
        black: 'bg-black text-white border-black/20',
        underline: 'border-b border-gray-500/20 text-foreground',
        purple:
          'bg-gradient-to-br from-purple-300/20 to-purple-500/12 text-purple-600 border-purple-500/40',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'default',
      clickable: false,
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  count?: number
  pressed?: boolean
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant = 'secondary',
      size,
      clickable = false,
      count,
      pressed = false,
      onClick,
      onKeyDown,
      children,
      ...props
    },
    ref
  ) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (clickable && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>)
      }
      onKeyDown?.(e)
    }

    const countBg = VARIANT_COUNT_BG[variant ?? 'secondary'] ?? 'bg-gray-600'

    return (
      <div
        ref={ref}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        className={cn(
          badgeVariants({ variant, size, clickable }),
          pressed && variant && VARIANT_PRESSED[variant],
          className
        )}
        aria-pressed={clickable ? pressed : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
        {count != null && (
          <span
            className={cn(
              'ml-1 w-5 h-5 rounded-full text-white text-xs font-medium inline-flex items-center justify-center',
              countBg
            )}
          >
            {count}
          </span>
        )}
      </div>
    )
  }
)
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
